import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { IoLocationOutline, IoMenu, IoCloseSharp } from "react-icons/io5";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchResult from "../../components/SearchFilters/SearchResult";
import { BASE_URL } from "../../constant/api";
import debounce from 'lodash/debounce';
import { FaFilter } from "react-icons/fa6";

// Memoized Filter Section Component
const FilterSection = memo(({ title, isOpen, onToggle, children }) => (
  <div className="mt-6">
    <div className="flex justify-between items-center cursor-pointer" onClick={onToggle}>
      <h3 className="font-medium">{title}</h3>
      <span>{isOpen ? "-" : "+"}</span>
    </div>
    {isOpen && <div className="mt-2">{children}</div>}
  </div>
));

FilterSection.displayName = 'FilterSection';

// Memoized Radio Input Component
const RadioInput = memo(({ id, name, checked, onChange, label }) => (
  <div className="flex items-center space-x-2">
    <input
      type="radio"
      id={id}
      name={name}
      checked={checked}
      onChange={onChange}
      className="mr-2"
    />
    <label htmlFor={id}>{label}</label>
  </div>
));

RadioInput.displayName = 'RadioInput';

// Memoized Location Selector Component
const LocationSelector = memo(() => (
  <div className="relative flex items-center">
    <IoLocationOutline className="absolute left-2 text-xl text-red-600" />
    <select className="appearance-none rounded-s-md border border-gray-300 py-2 pl-8 pr-4 focus:outline-none">
      <option value="">Ajman, UAE</option>
      <option value="">Dubai, UAE</option>
      <option value="">Sharjah, UAE</option>
    </select>
  </div>
));

LocationSelector.displayName = 'LocationSelector';

function Search() {
  // State management
  const [searchResult, setSearchResult] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [subCategories, setSubCategories] = useState({});
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedSubCategories, setSelectedSubCategories] = useState({});
  const [isExpanded, setIsExpanded] = useState({
    categories: false,
    price: false,
    rating: false,
    brand: false
  });
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [minRating, setMinRating] = useState(0);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("query");

  // Memoized API calls
  const fetchData = useCallback(async (url, setter) => {
    try {
      const response = await axios.get(url);
      const data = response.data.Data || response.data.data;
      // console.log(response,url);
      if (data) {
        setter(data);
      }
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
    }
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        try {
          const response = await axios.post(`${BASE_URL}/products/search/`, {
            search_word: query,
          });
          setSearchResult(response.data.Data);
        } catch (error) {
          console.error("Error fetching search results:", error);
          setSearchResult([]);
        }
      }, 300),
    []
  );

  // Event handlers
  const toggleSection = useCallback((section) => {
    setIsExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handlePriceChange = useCallback((type, value) => {
    setPriceRange(prev => ({
      ...prev,
      [type]: value
    }));
  }, []);

  const handleRatingChange = useCallback((rating) => {
    setMinRating(rating);
  }, []);

  const handleCategoryChange = useCallback((categoryId) => {
    setSelectedCategories(prev => prev === categoryId ? null : categoryId);
    setSelectedSubCategories({});
  }, []);

  const handleSubCategoryChange = useCallback((categoryId, subCategoryId) => {
    setSelectedSubCategories(prev => ({
      ...prev,
      [categoryId]: prev[categoryId] === subCategoryId ? null : subCategoryId
    }));
  }, []);

  // Effects
  useEffect(() => {
    if (query) {
      setSearchQuery(query);
      debouncedSearch(query);
    }
    return () => debouncedSearch.cancel();
  }, [query, debouncedSearch]);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/products/`);
        setSearchResult(response.data.Data || response.data.data);
        
      } catch (error) {
        console.error("Error fetching all products:", error);
        setSearchResult([]);
      }
    };


    if (searchQuery.length > 0) {
      debouncedSearch(searchQuery);
      navigate(`?query=${searchQuery}`, { replace: true });
    } else {
      debouncedSearch.cancel();
      fetchAllProducts();
      navigate(`?`, { replace: true });
    }
  }, [searchQuery, navigate, debouncedSearch]);

  useEffect(() => {
    fetchData(`${BASE_URL}/categories/`, setCategories);
    fetchData(`${BASE_URL}/products/brand/`, setBrands);
  }, [fetchData]);

  useEffect(() => {
    if (selectedCategories) {
      fetchData(
        `${BASE_URL}/categories/${selectedCategories}/subcategory/`,
        (subcategories) => {
          setSubCategories(prev => ({
            ...prev,
            [selectedCategories]: subcategories,
          }));
        }
      );
    }
  }, [selectedCategories, fetchData]);

  // Memoized filter function
  const filteredResults = useMemo(() => {
    return searchResult.filter(product => {
      const categoryMatch = !selectedCategories ||
        (selectedCategories === product.category &&
          (!selectedSubCategories[selectedCategories] ||
            selectedSubCategories[selectedCategories] === product.subcategory));

      const brandMatch = !selectedBrand || product.brand === selectedBrand;

      const priceMatch = (!priceRange.min || product.price >= Number(priceRange.min)) &&
        (!priceRange.max || product.price <= Number(priceRange.max));

      const ratingMatch = product.product_rating >= minRating;

      return categoryMatch && brandMatch && priceMatch && ratingMatch;
    });
  }, [searchResult, selectedCategories, selectedSubCategories, selectedBrand, priceRange, minRating]);

  // Add reset filters handler
  const handleResetFilters = useCallback(() => {
    setSelectedCategories(null);
    setSelectedBrand(null);
    setSelectedSubCategories({});
    setPriceRange({ min: "", max: "" });
    setMinRating(0);
  }, []);

  // Memoized Filter Components
  const CategoryFilters = memo(() => (
    <FilterSection
      title="Categories"
      isOpen={isExpanded.categories}
      onToggle={() => toggleSection('categories')}
    >
      {categories.map((category) => (
        <div key={category.id} className="mt-2">
          <RadioInput
            id={`category-${category.id}`}
            name="category"
            checked={selectedCategories === category.id}
            onChange={() => handleCategoryChange(category.id)}
            label={category.name}
          />
          {selectedCategories === category.id && subCategories[category.id]?.map((subCategory) => (
            <div key={subCategory.id} className="ml-4 mt-2">
              <RadioInput
                id={`subcategory-${subCategory.id}`}
                name={`subcategory-${category.id}`}
                checked={selectedSubCategories[category.id] === subCategory.id}
                onChange={() => handleSubCategoryChange(category.id, subCategory.id)}
                label={subCategory.name}
              />
            </div>
          ))}
        </div>
      ))}
    </FilterSection>
  ));

  CategoryFilters.displayName = 'CategoryFilters';

  const PriceFilters = memo(() => (
    <FilterSection
      title="Price Range"
      isOpen={isExpanded.price}
      onToggle={() => toggleSection('price')}
    >
      <div className="space-y-2">
        <input
          type="number"
          placeholder="Min Price"
          value={priceRange.min}
          onChange={(e) => handlePriceChange("min", e.target.value)}
          className="w-full p-2 border rounded"
        />
        <input
          type="number"
          placeholder="Max Price"
          value={priceRange.max}
          onChange={(e) => handlePriceChange("max", e.target.value)}
          className="w-full p-2 border rounded"
        />
      </div>
    </FilterSection>
  ));

  PriceFilters.displayName = 'PriceFilters';

  const RatingFilters = memo(() => (
    <FilterSection
      title="Rating"
      isOpen={isExpanded.rating}
      onToggle={() => toggleSection('rating')}
    >
      <div className="flex flex-col space-y-2">
        {[0, 1, 2, 3, 4, 5].map((rating) => (
          <RadioInput
            key={rating}
            id={`rating-${rating}`}
            name="rating"
            checked={minRating === rating}
            onChange={() => handleRatingChange(rating)}
            label={rating === 0 ? 'All' : `${rating} Star${rating === 1 ? '' : 's'} & Above`}
          />
        ))}
      </div>
    </FilterSection>
  ));

  RatingFilters.displayName = 'RatingFilters';

  const BrandFilters = memo(() => (
    <FilterSection
      title="Brands"
      isOpen={isExpanded.brand}
      onToggle={() => toggleSection('brand')}
    >
      <div className="flex flex-col space-y-2">
        <RadioInput
          id="brand-all"
          name="brand"
          checked={selectedBrand === null}
          onChange={() => setSelectedBrand(null)}
          label="All Brands"
        />
        {brands.map((brand) => (
          <RadioInput
            key={brand.id}
            id={`brand-${brand.id}`}
            name="brand"
            checked={selectedBrand === brand.id}
            onChange={() => setSelectedBrand(brand.id)}
            label={brand.name}
          />
        ))}
      </div>
    </FilterSection>
  ));

  BrandFilters.displayName = 'BrandFilters';

  // Mobile Sidebar Component
  const MobileFilterSidebar = memo(() => (
    <div
      className={`fixed inset-0 z-50 transform ${isMobileFilterOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 ease-in-out lg:hidden`}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={() => setIsMobileFilterOpen(false)}
      />
      <div className="absolute inset-y-0 left-0 w-64 bg-white shadow-lg overflow-y-auto">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Filters</h2>
            <button
              onClick={() => setIsMobileFilterOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <IoCloseSharp className="text-xl" />
            </button>
          </div>
          <CategoryFilters />
          <PriceFilters />
          <RatingFilters />
          <BrandFilters />
          <button
            onClick={handleResetFilters}
            className="mt-6 w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
          >
            Reset Filters
          </button>
        </div>
      </div>
    </div>
  ));

  MobileFilterSidebar.displayName = 'MobileFilterSidebar';

  return (
    <div>
      {/* Search Header */}
      <div className="relative w-full border-t-2">

        <div className="flex w-full max-w-full items-center justify-between rounded-b-lg bg-brand-primary p-3 md:ms-10 md:w-1/2">
          <button
            onClick={() => setIsMobileFilterOpen(true)}
            className="lg:hidden p-2"
          >
            <FaFilter className="text-2xl text-brand-light-gray " />
          </button>
          <div className="flex flex-grow items-center">
            <LocationSelector />
            <div className="relative flex-grow">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                className="w-full rounded-e-md border border-gray-300 py-2 pl-4 focus:outline-none md:max-w-[250px]"
              />
            </div>
          </div>

        </div>
      </div>

      <MobileFilterSidebar />

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row p-4 space-x-0 lg:space-x-10">
        {/* Desktop Filters */}
        <div className="hidden lg:block lg:w-[250px] mb-8 ml-10 mt-10 lg:mb-0">
          <div className="flex justify-between items-center">
            <h2 className="m-0 text-xl font-semibold">Filter</h2>
            {/* Add Reset Button in Desktop */}
            <button
              onClick={handleResetFilters}
              className="text-red-600 hover:text-red-700 text-sm"
            >
              Reset All
            </button>
          </div>
          <CategoryFilters />
          <PriceFilters />
          <RatingFilters />
          <BrandFilters />
        </div>


        {/* Search Results */}
        <SearchResult searchResult={filteredResults} />
      </div>
    </div>
  );
}

export default memo(Search);